import moment from 'moment';
import { Inject } from '@odinjs/odin';

import { FetchAPI, FieldAPI, FormAPI, InputAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeSubmit, OnCancel, OnMount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService, MessageService, Validator } from '@philips-internal/oswald-ui-core/lib/services';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';

import { AbortErrorInterface, FormInterface, SuspendResponseBody } from './suspend-item-types';
import { MedicineAndSolutionsParams } from '../medicine-and-solutions/medicine-and-solutions-params';
import { PrescriptionType } from '../../services/timeline-service';
import BaseController from '../../../BaseController';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { FragmentNames, MedicationSuspendAliases, SuspendItemAliases } from '../../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { ApplicationRoute } from '../../../../constants/routes';
import timeService from '../../../../services/time-service';
import { ADEP_E1113Parameters, FunctionName } from '../../../../utils/parameters';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { YesNoType } from '../../../../utils/enum-utils';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { MedicationViewerParams } from '../medication-viewer/medication-viewer-params';

enum SolutionType {
  BLOOD_MANAGEMENT = 3,
  ADULT_PARENTERAL_NUTRITION = 5
}

@Controller({ component: SuspendItemAliases.PANEL_SUSPEND_ITEM, fragment: FragmentNames.SUSPEND_ITEM })
class ItemSuspendController extends BaseController {

  private validatorRequired = [
    {
      name: 'required'
    }
  ] as Validator[]

  @Inject(SuspendItemAliases.FORM_SUSPEND_ITEM)
  private formApi!: FormAPI;

  @Inject(SuspendItemAliases.FIELD_REASON)
  private fieldReasonApi!: FieldAPI;

  @Inject(SuspendItemAliases.FIELD_TEXT_AREA_JUSTIFICATION)
  private fieldJustificationApi!: FieldAPI;

  @OnMount(SuspendItemAliases.FETCH_SUSPEND_ITEM_REASON)
  handleMountSuspendItemfetch(event: MountEvent): void {
    const fetchReason = event.target as FetchAPI;
    const establishmentCode = foundationContext.getEstablishment()?.id;
    fetchReason.setParameters({ establishmentCode, status: MedicineAndSolutionsParams.params.ieTypeItem }, { skip: true });
  }

  @OnMount(SuspendItemAliases.PANEL_SUSPEND_ITEM)
  handleMountSuspendItemLayout(): void {
    InformationBarUtils.collapsedBar = true;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    this.context.set({
      encounterId,
      patientId,
    });
  }

  @OnMount(SuspendItemAliases.FORM_SUSPEND_ITEM)
  handleMountFormSuspendItem(): void {
    const formRecord = this.formApi.getRecord();

    this.formApi.setRecord({
      ...formRecord,
      administrationRouteDescription: String(MedicineAndSolutionsParams.params.ieViaApplicationDescription),
      amountAdministered: String(MedicineAndSolutionsParams.params.amountAdministered),
      description: String(MedicineAndSolutionsParams.params.dsMaterialId),
      unitMeasureDoseDescription: String(MedicineAndSolutionsParams.params.unitMeasureDoseDescription)
    });
  }

  @OnMount(SuspendItemAliases.INPUT_CURRENT_TIME)
  handleMountCurrentTimeInput({ target }: EventData<InputAPI>): void {
    const allowsEditingTheTimeOfPerformingTheActions = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOWS_EDITING_THE_TIME_OF_PERFORMING_THE_ACTIONS);

    timeService.getCurrentDateTime()
      .then(date => target.setValue(date))
      .catch(() => target.setValue(moment().format(ISO_FULL_DATE_FORMAT)))
      .finally(() => {
        if (PrescriptionType.BLOOD_MANAGEMENT === MedicineAndSolutionsParams.params.ieTypeItem) {
          return
        }
        target.setEnabled(BooleanUtils.isTrueValue(allowsEditingTheTimeOfPerformingTheActions as string))
      })
  }

  @OnMount(SuspendItemAliases.FIELD_REASON)
  handleMountFieldReason({ target }: EventData<FieldAPI>): void {
    target.setVisible(!this.isOralSupplementType())
  }

  @OnMount([SuspendItemAliases.DOSE_EXECUTED_FIELD, SuspendItemAliases.UNITY_EXECUTED_FIELD])
  handleMountDoseExecutedFields({ target }: EventData<FieldAPI>): void {
    target.setVisible(this.isOralSupplementType())
  }

  @OnMount(SuspendItemAliases.FIELD_ADMINISTRATION_ROUTE_TYPE)
  handleMountURouteTypeedFields({ target }: EventData<FieldAPI>): void {
    if (PrescriptionType.BLOOD_MANAGEMENT === MedicineAndSolutionsParams.params.ieTypeItem
      || PrescriptionType.RECOMMENDATIONS === MedicineAndSolutionsParams.params.ieTypeItem
      || PrescriptionType.NURSING_INTERVENTIONS === MedicineAndSolutionsParams.params.ieTypeItem
      || PrescriptionType.ADULT_PARENTERAL_NUTRITION === MedicineAndSolutionsParams.params.ieTypeItem) {
      target.setVisible(false)
    }
  }

  @OnMount(SuspendItemAliases.FIELD_CURRENT_TIME)
  handleMountCurrentTimeField({ target }: EventData<FieldAPI>): void {
    target.setVisible(!this.isEnteralNutritionType())
  }

  @OnValidate(SuspendItemAliases.FIELD_REASON)
  handleValidateReasonField(): void {
    const requireReasonToSuspend = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.REQUIRE_REASON_TO_SUSPEND);
    const isBoth = 'A';
    const hasValidation = requireReasonToSuspend === YesNoType.YES || requireReasonToSuspend === isBoth;

    if (hasValidation) {
      this.fieldReasonApi.setValidators(this.validatorRequired);
    }
  }

  @OnValidate(MedicationSuspendAliases.FIELD_TEXT_AREA_JUSTIFICATION)
  handleValidateJustificationFieldValidation(): void {
    const requireJustification = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.REQUIRE_JUSTIFICATION_ON_SUSPEND_ITEM);
    const hasValidation = requireJustification === YesNoType.YES;

    if (hasValidation && !this.isEnteralNutritionType()) {
      this.fieldJustificationApi.setValidators(this.validatorRequired);
    }
  }

  @OnCancel(SuspendItemAliases.FORM_SUSPEND_ITEM)
  handleCancelSuspendItemForm(): void {
    applicationContext.popNavigationItem();
  }

  @OnBeforeSubmit(SuspendItemAliases.FORM_SUSPEND_ITEM)
  handleSubmitSuspendItemForm(): void {
    const formRecord = this.formApi.getRecord() as unknown as FormInterface;
    const itemType = MedicineAndSolutionsParams.params.ieTypeItem;

    switch (itemType) {
      case PrescriptionType.BLOOD_MANAGEMENT:
        this.suspendSolution(formRecord, SolutionType.BLOOD_MANAGEMENT);
        break;
      case PrescriptionType.ADULT_PARENTERAL_NUTRITION:
        this.suspendSolution(formRecord, SolutionType.ADULT_PARENTERAL_NUTRITION);
        break;
      case PrescriptionType.ENTERAL_NUTRITION:
        this.suspendEnteralNutrition(formRecord);
        break;
      default:
        this.suspendItem(formRecord);
        break;
    }
  }

  private suspendItem({ currentTime, justification, reason }: FormInterface): void {
    const alterationDate = moment(String(currentTime)).utc().format(ISO_FULL_DATE_FORMAT);
    const eventDate = moment(MedicineAndSolutionsParams.params.startId).utc().format(ISO_FULL_DATE_FORMAT)

    const requestBody: Record<string, Value> = {
      alterationDate,
      encounterNumber: MedicineAndSolutionsParams.params.encounterId,
      eventDate,
      hourSequence: MedicineAndSolutionsParams.params.hourSequence,
      intervalCode: MedicineAndSolutionsParams.params.cdIntervalId,
      itemCode: MedicineAndSolutionsParams.params.cdMaterialId,
      itemSequence: MedicineAndSolutionsParams.params.nrSequenceId,
      itemType: MedicineAndSolutionsParams.params.ieTypeItem,
      justifyDescription: justification || '',
      prescriptionNumber: MedicineAndSolutionsParams.params.nrPrescriptionId,
      reasonSuspendSequence: reason?.sequenceNumber,
      scheduleSequence: MedicineAndSolutionsParams.params.hourSequence,
      suspensionReasonSequence: reason?.sequenceNumber,
    }

    let valueEndPoint = Endpoint.ADEP_SUSPEND_MEDICATION;

    if (PrescriptionType.RECOMMENDATIONS === MedicineAndSolutionsParams.params.ieTypeItem) {
      valueEndPoint = Endpoint.ADEP_SUSPEND_RECOMMENDATION;
    } else if (PrescriptionType.NURSING_INTERVENTIONS === MedicineAndSolutionsParams.params.ieTypeItem) {
      valueEndPoint = Endpoint.ADEP_SUSPEND_NURSING_INTERVENTIONS;
    }

    HttpService.post(valueEndPoint, {}, { body: requestBody })
      .then((response) => {
        const responseBody = response.body as SuspendResponseBody;
        if (responseBody.messageDescription) {
          this.showToast(responseBody.messageDescription, ToastLevel.WARNING);
          return
        }
        this.showToast('label.medication-suspend.suspend-success', ToastLevel.SUCCESS);
      }).catch((error) => {
        this.showErrorMessages(error as AbortErrorInterface);
      }).finally(() => {
        const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
        applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, {
          encounterId,
          patientId,
        });
        applicationContext.clearNavigationStack();
      })
  }

  private suspendSolution({ justification, reason }: FormInterface, solutionType: number): void {
    const body: Record<string, Value> = {
      itemCode: MedicationViewerParams.params?.itemCode ?? MedicineAndSolutionsParams.params.cdMaterialId,
      justification: justification || '',
      prescriptionNumber: Number(MedicineAndSolutionsParams.params.nrPrescriptionId),
      solutionSequence: MedicineAndSolutionsParams.params.nrSequenceId,
      solutionType,
      suspendReasonSequence: reason?.sequenceNumber
    }
    const {
      patientId,
      encounterId
    } = this.historyService.getCurrentRouteParams();
    HttpService.put(Endpoint.ADEP_SUSPEND_BLOOD_MANAGEMENT, {}, { body })
      .then(() => {
        this.showToast('label.medication-suspend.suspend-success', ToastLevel.SUCCESS);
        applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, {
          encounterId,
          patientId,
        });
        applicationContext.clearNavigationStack();
      })
  }

  private showErrorMessages(abortError: AbortErrorInterface): void {
    if (abortError.body && abortError.body.errors) {
      abortError.body.errors.forEach(errorMessage => this.showToast(errorMessage, ToastLevel.ERROR));
    }
  }

  private showToast(message: string, toastLevel: ToastLevel): void {
    const toastId = 'suspend-item-toast'
    ToastService.close(toastId)
    ToastService.open({
      dismissible: true,
      id: toastId,
      level: toastLevel,
      message: MessageService.translate(message),
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
    })
  }

  private suspendEnteralNutrition({ justification, reason }: FormInterface): void {
    const body: Record<string, Value> = {
      justification: justification || '',
      reasonSequence: reason?.sequenceNumber,
      scheduleSequence: MedicineAndSolutionsParams.params.hourSequence,
    };
    const {
      patientId,
      encounterId
    } = this.historyService.getCurrentRouteParams();
    HttpService.post(Endpoint.EMAR_SUSPEND_ENTERAL_NUTRITION, {}, { body })
      .then(() => {
        this.showToast('label.medication-suspend.suspend-success', ToastLevel.SUCCESS);
        applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, {
          encounterId,
          patientId,
        });
        applicationContext.clearNavigationStack();
      }).catch((error) => {
        this.showErrorMessages(error as AbortErrorInterface);
      })
  }

  private isOralSupplementType(): boolean {
    return PrescriptionType.ORAL_SUPPLEMENTS === MedicineAndSolutionsParams.params.ieTypeItem
  }

  private isEnteralNutritionType(): boolean {
    return PrescriptionType.ENTERAL_NUTRITION === MedicineAndSolutionsParams.params.ieTypeItem
  }
}

export default ItemSuspendController;
